<script setup>
import { ref, watch, onMounted, reactive } from 'vue';
import { useToast } from "primevue/usetoast";
import { callAPI, DRUPAL_API_ROUTE } from "../service/DrupalService";
import { useI18n } from 'vue-i18n'

const { t } = useI18n();

const toast = useToast();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});
const emit = defineEmits(['update:visible']);;

const localVisible = ref(props.visible);
const crsf = ref();
const user = ref();

const initialState = {
  domainsLoading: false,
  domain: '',
  domainsList: [],
  domainDisabled: false,
  bulkFileUploadURL: DRUPAL_API_ROUTE.BULK_UPLOAD,
  bulkFile: '',
  bulkCreateLoading: false,
  bulksList: [],
  bulkRunning: false,
  bulksLoading: false

};

const formData = reactive({ ...initialState });

const home = ref({
  icon: 'pi pi-home'
});
const items = ref([
  {
    label: t('bulk.title')
  }
]);

const getBulks = async () => {
  formData.bulksLoading = true;
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.BULKS);
    formData.bulksList = response.data;

    formData.bulkRunning = formData.bulksList.find(bulk => bulk.status === 0) !== undefined;
  } catch (err) {
    console.log(err);
  } finally {
    formData.bulksLoading = false;
  }
};


const fetchDomains = async () => {
  formData.domainsLoading = true;
  var currentWorkspace = user.value.current_workspace;
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.WORKSPACES_DOMAINS_LIST, { workspace: currentWorkspace });
    formData.domainsList = response.data;

    var workspaceData = JSON.parse(localStorage.getItem('workspaces'));
    var currentWorkspaceData = workspaceData.find(item => item.id === currentWorkspace);
    formData.domain = currentWorkspaceData.default_domain;

    formData.domainsLoading = false;
  } catch (err) {
    console.log(err);
  }
};

const onUpload = async (event) => {
  var response = JSON.parse(event.xhr.response);

  formData.bulkFile = response;
  toast.add({ severity: 'success', summary: t('bulk.title'), detail: t('bulk.upload.message.validation'), life: 5000 });
};

const onErrorSend = async (event) => {
  var response = JSON.parse(event.xhr.response);
  toast.add({ severity: 'error', summary: t('bulk.title'), detail: response.error, life: 5000 });

}

const bulkGenerate = async () => {
  formData.bulkCreateLoading = true;
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.BULK_CREATE, {}, { domain: formData.domain });
    console.log(response)
  } catch (err) {
    console.log(err);
  }
  toast.add({ severity: 'success', summary: t('bulk.title'), detail: t('bulk.upload.message.generate'), life: 5000 });
  handleHide();
}

const bulkTemplateDownload = async (uuid) => {
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.BULK_TEMPLATE);
    var fileUrl = response.data;

    var link = document.createElement('a');
    link.href = fileUrl;
    link.click();
  } catch (err) {
    console.log(err);
  } finally {
    bulksLoadingDownload.value = false;
  }
}

const clearCallback = async () => {
  formData.bulkFile = '';
}

function resetForm() {
  Object.assign(formData, initialState);
}

async function handleShow() {
  await getBulks();
  user.value = JSON.parse(localStorage.getItem('user'));
  await fetchDomains();
  var response = await callAPI(DRUPAL_API_ROUTE.CRSF_TOKEN);
  crsf.value = response.data;
}

async function onBeforeSend(request) {
  request.xhr.setRequestHeader('X-CSRF-Token', crsf.value);
}

function handleHide() {
  resetForm();
  emit('update:visible', false);
}

watch(
  () => props.visible,
  (newValue) => {
    localVisible.value = newValue;
  }
)
</script>


<template>
  <Fluid>
    <Drawer :blockScroll="true" @hide="handleHide" @show="handleShow" v-model:visible="localVisible">
      <template #header>
        <div class="flex items-start gap-2 text-xs">
          <Breadcrumb :home="home" :model="items" class="!p-0">
            <template #item="{ item }">
              <span v-if="item.label" class="text-primary mr-2">{{ item.label }}</span>
              <span :class="item.icon"></span>
            </template>
            <template #separator><i class="pi pi-angle-right"></i></template>
          </Breadcrumb>
        </div>
      </template>
      <div class="flex mx-auto">
        <div class="flex flex-col">
          <form v-if="!formData.bulkRunning" @submit.prevent="generatePage" class="space-y-10">
            <div class="flex flex-col gap-2 space-y-4">
              <div class="space-y-6">
                <div v-if="!formData.bulksLoading && !formData.bulkFile" class="flex flex-col items-start gap-2">
                  <Message severity="secondary" variant="simple">{{ t('bulk.messages.notice') }}</Message>
                  <div><Button class="w-1/2" icon="pi pi-download" @click="bulkTemplateDownload" label="Télécharger" />
                  </div>
                  <hr />
                </div>
                <div class="flex flex-col gap-2">
                  <label for="domain">{{ t('bulk.labels.domain') }}</label>
                  <Skeleton v-if="formData.bulksLoading || formData.domainsLoading" height="3rem" class="mb-2">
                  </Skeleton>
                  <Select v-else v-model="formData.domain" :options="formData.domainsList" optionValue="uuid"
                    optionLabel="domain" :placeholder="t('bulk.placeholder.domain')" :disabled="formData.domainDisabled" />
                </div>
              </div>
              <div class="flex flex-col gap-2 space-y-4">
                <div class="space-y-6">
                  <div class="flex flex-col gap-4">
                    <div class="flex flex-col items-start gap-2">
                      <label for="domain">{{ t('bulk.labels.file') }}</label>
                      <Skeleton v-if="formData.bulksLoading || formData.domainsLoading" height="3rem" class="mb-2">
                      </Skeleton>
                      <div v-else-if="!formData.bulksLoading && !formData.bulkFile">
                        <FileUpload @error="onErrorSend" @before-send="onBeforeSend" :withCredentials="true"
                          mode="basic" name="file" :url="formData.bulkFileUploadURL" accept="text/csv"
                          :maxFileSize="1000000" @upload="onUpload" :auto="true" :chooseLabel="t('common.labels.browse')" />
                      </div>
                      <div v-else class="flex items-center space-x-4">
                        <div>{{ formData.bulkFile }}</div>
                        <div><Button @click="clearCallback()" icon="pi pi-times" rounded outlined
                            severity="danger"></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
          <div v-if="formData.bulkRunning && !formData.bulksLoading" class="mt-1">
            <Message severity="info">{{ t('bulk.messages.current') }}</Message>
          </div>
        </div>
      </div>
      <template #footer>
        <div v-if="!formData.bulksLoading && !formData.bulkRunning" class="flex items-center gap-2">
          <Button @click="handleHide" :label="t('common.labels.cancel')" class="flex-auto" outlined />
          <Button type="submit" @click="bulkGenerate" :loading="formData.bulkCreateLoading" :label="t('common.labels.create')" />
        </div>
        <div v-else>
          <Button @click="handleHide" :label="t('common.labels.close')" class="flex-auto" outlined />
        </div>
      </template>
    </Drawer>
  </Fluid>
</template>
